import {ApiBase, SearchCriteria, toQuery} from '@ozark/common';
import {ActiveFilter} from '@ozark/common/components';
import {
  AgentPayoutResponse,
  AgentResidual,
  GroupAgentResponse,
  GroupResidual,
  PaginatedGroupAgentResponse,
  ReferralOverrideResidual,
  ResidualGroupOverride,
  ResidualOverview,
  ResidualPayoutReport,
} from '@ozark/functions/src/functions/express/private/types/Residual';
import {PaginatedResponse} from '@ozark/functions/src/shared';
import {CancelToken} from 'axios';

export class ResidualsApi extends ApiBase {
  private getAgentParam = (agentId?: string) => {
    return agentId ? `firestoreAgentId__eq=${agentId}&` : '';
  };
  private getAgentsParam = (agentIds?: string[]) => {
    return agentIds && agentIds.length > 0 ? `firestoreAgentIds__in=${agentIds.join(',')}&` : '';
  };
  private getGroupParam = (groupId?: string) => {
    return groupId ? `firestoreGroupId__eq=${groupId}&` : '';
  };
  getResidualExport = (yearMonth: string) => {
    return this.get<any[]>(`residuals/export?yearMonth__eq=${yearMonth}`);
  };
  getResidualPayoutReport = (yearMonth: string, cancelToken?: CancelToken) => {
    return this.get<ResidualPayoutReport>(
      `residuals/profits?yearMonth__eq=${yearMonth}`,
      undefined,
      cancelToken
    );
  };
  getGroupResidualOverview = () => {
    return this.get<ResidualOverview[]>(`residuals/overview`);
  };
  getGroupResiduals = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<PaginatedResponse<GroupResidual>>(
      `residuals/group?${this.getAgentParam(agentId)}${queryString}`
    );
  };
  getGroupAgentAggregateResiduals = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<PaginatedGroupAgentResponse>(
      `residuals/group_agent_aggregate?${this.getAgentParam(agentId)}${queryString}`
    );
  };
  getGroupResidualsExport = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<GroupResidual[]>(
      `residuals/group/export?${this.getAgentParam(agentId)}${queryString}`
    );
  };
  getGroupAgentAggregateResidualsExport = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<GroupAgentResponse[]>(
      `residuals/group_agent_aggregate/export?${this.getAgentParam(agentId)}${queryString}`
    );
  };
  getAgentResiduals = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<PaginatedResponse<AgentResidual>>(
      `residuals/agent?${this.getAgentParam(agentId)}${queryString}`
    );
  };
  getAgentResidualsExport = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    return this.get<AgentResidual[]>(
      `residuals/agent/export?${this.getAgentParam(agentId)}${queryString}`
    );
  };

  getGroupOverrides = (yearMonth: string, cancelToken?: CancelToken) => {
    return this.get<ResidualGroupOverride[]>(
      `residuals/group_overrides?yearMonth__eq=${yearMonth}`,
      undefined,
      cancelToken
    );
  };

  getGroupOverridesExport = (groupId: string, childGroupId: string, yearMonth: string) => {
    return this.get<[]>(
      `residuals/group_overrides/export?groupId__eq=${groupId}&childGroupId__eq=${childGroupId}&yearMonth__eq=${yearMonth}`
    );
  };

  getEquipmentExpenseResiduals = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string,
    groupId?: string,
    cancelToken?: CancelToken
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    const agentParam = this.getAgentParam(agentId);
    const groupParam = this.getGroupParam(groupId);

    return this.get<AgentResidual[]>(
      `residuals/equipment_expense?${agentParam}${groupParam}${queryString}`,
      {},
      cancelToken
    );
  };
  getEquipmentExpenseResidualsExport = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string,
    groupId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    const agentParam = this.getAgentParam(agentId);
    const groupParam = this.getGroupParam(groupId);

    return this.get<AgentResidual[]>(
      `residuals/equipment_expense/export?${agentParam}${groupParam}${queryString}`
    );
  };
  getReferralOverridesResiduals = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string,
    groupId?: string,
    cancelToken?: CancelToken
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    const agentParam = this.getAgentParam(agentId);
    const groupParam = this.getGroupParam(groupId);

    return this.get<ReferralOverrideResidual[]>(
      `residuals/referral_overrides?${agentParam}${groupParam}${queryString}`,
      {},
      cancelToken
    );
  };
  getReferralOverridesResidualsExport = (
    searchCriteria: SearchCriteria,
    filters?: ActiveFilter[],
    agentId?: string,
    groupId?: string
  ) => {
    const queryString = toQuery(searchCriteria, filters);
    const agentParam = this.getAgentParam(agentId);
    const groupParam = this.getGroupParam(groupId);

    return this.get<ReferralOverrideResidual[]>(
      `residuals/referral_overrides/export?${agentParam}${groupParam}${queryString}`
    );
  };
  getAgentPayoutResiduals = (
    filters?: ActiveFilter[],
    agentIds?: string[],
    cancelToken?: CancelToken
  ) => {
    const queryString = toQuery(null, filters);
    const agentParam = this.getAgentsParam(agentIds);

    return this.get<AgentPayoutResponse>(
      `residuals/agent_payout?${agentParam}${queryString}`,
      {},
      cancelToken
    );
  };
}
